<!--
  @component Navbar.MenuToggle

  A toggle button component for the navbar menu. It handles the open/close state of the menu
  and provides visual feedback. This component is typically used in responsive designs for smaller screens.

  @prop css - Custom CSS styles to apply to the toggle button

  Example:
  <Navbar.MenuToggle />
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { menuToggleStyles } from '../navbar.styles';
  import type { NavbarStore } from '../navbar-store.svelte';
  import { visuallyHidden } from '@agapi/ui/panda/patterns';
  import type { NavbarMenuToggleProps } from '../navbar.types';

  const { css: cssProps }: NavbarMenuToggleProps = $props();

  /**
   * The navbar store context, providing access to the navbar state and actions.
   */
  const navbarStore = getContext<NavbarStore>('navbar');

  /**
   * Computed styles for the menu toggle button, based on the open state of the menu.
   */
  const styles = $derived(menuToggleStyles.raw({ open: navbarStore.isMenuOpen }));

  /**
   * Handles the toggle action when the button is clicked.
   * Toggles the menu open/closed state in the navbar store.
   */
  const onclick = () => {
    navbarStore.toggleMenu();
  };
</script>

<button
  {onclick}
  class={css(styles.root, cssProps)}
  aria-label={navbarStore.isMenuOpen ? 'Close menu' : 'Open menu'}
  aria-expanded={navbarStore.isMenuOpen}
  aria-controls="navbar-menu"
  data-type="menu-toggle"
  type="button"
>
  <span class={css(styles.line)} aria-hidden="true"></span>
  <span class={css(styles.line)} aria-hidden="true"></span>
  <span class={visuallyHidden()}>{navbarStore.isMenuOpen ? 'Close menu' : 'Open menu'}</span>
  <span class={css(styles.line)} aria-hidden="true"></span>
</button>
